"use client";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SMARTDWARA TOAST PROVIDER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Enterprise-grade toast notification component styled like Stripe/Vercel.
 * Place this component at the root of your application (layout.tsx).
 *
 * USAGE:
 *   // In app/layout.tsx
 *   import { ToastProvider } from '@/components/ui/ToastProvider';
 *
 *   export default function RootLayout({ children }) {
 *     return (
 *       <html>
 *         <body>
 *           {children}
 *           <ToastProvider />
 *         </body>
 *       </html>
 *     );
 *   }
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { Toaster } from "sonner";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Loader2,
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════
// CUSTOM ICONS
// ═══════════════════════════════════════════════════════════════════════════

const ToastIcons = {
  success: (
    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20">
      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
    </div>
  ),
  error: (
    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500/20">
      <XCircle className="w-4 h-4 text-red-500" />
    </div>
  ),
  warning: (
    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/20">
      <AlertTriangle className="w-4 h-4 text-amber-500" />
    </div>
  ),
  info: (
    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/20">
      <Info className="w-4 h-4 text-blue-500" />
    </div>
  ),
  loading: (
    <div className="flex items-center justify-center w-5 h-5">
      <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
    </div>
  ),
};

// ═══════════════════════════════════════════════════════════════════════════
// TOAST PROVIDER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

interface ToastProviderProps {
  /**
   * Position of toast notifications
   * @default "top-right"
   */
  position?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";

  /**
   * Theme mode
   * @default "dark" (matches SmartDwara's dark theme)
   */
  theme?: "light" | "dark" | "system";

  /**
   * Maximum number of toasts to show at once
   * @default 3
   */
  visibleToasts?: number;

  /**
   * Whether to show close button on toasts
   * @default true
   */
  closeButton?: boolean;

  /**
   * Enable rich colors for different toast types
   * @default true
   */
  richColors?: boolean;

  /**
   * Expand toasts on hover
   * @default true
   */
  expand?: boolean;
}

export function ToastProvider({
  position = "top-right",
  theme = "dark",
  visibleToasts = 3,
  closeButton = true,
  richColors = true,
  expand = true,
}: ToastProviderProps) {
  return (
    <Toaster
      position={position}
      theme={theme}
      visibleToasts={visibleToasts}
      closeButton={closeButton}
      richColors={richColors}
      expand={expand}
      gap={12}
      offset={16}
      icons={ToastIcons}
      toastOptions={{
        // Base styles for all toasts
        style: {
          background: "rgba(26, 26, 26, 0.95)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: "12px",
          padding: "14px 16px",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05)",
          color: "#FAFAFA",
          fontSize: "14px",
          fontWeight: "500",
          lineHeight: "1.5",
          maxWidth: "400px",
        },

        // Class names for different states
        classNames: {
          toast:
            "group toast-item font-sans !bg-[#1A1A1A]/95 !border-white/10 !backdrop-blur-xl",
          title: "!text-white !font-medium !text-sm",
          description: "!text-gray-400 !text-xs !mt-1",
          actionButton:
            "!bg-white !text-black !font-semibold !text-xs !px-3 !py-1.5 !rounded-lg hover:!bg-gray-100 !transition-colors",
          cancelButton:
            "!bg-transparent !text-gray-400 !font-medium !text-xs hover:!text-white !transition-colors",
          closeButton:
            "!bg-white/5 !border-white/10 hover:!bg-white/10 !text-gray-400 hover:!text-white !transition-all",

          // Type-specific classes
          success: "!border-l-2 !border-l-emerald-500 !bg-emerald-500/5",
          error: "!border-l-2 !border-l-red-500 !bg-red-500/5",
          warning: "!border-l-2 !border-l-amber-500 !bg-amber-500/5",
          info: "!border-l-2 !border-l-blue-500 !bg-blue-500/5",
          loading: "!border-l-2 !border-l-gray-500",
        },

        // Duration defaults per type
        duration: 4000,
      }}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ADDITIONAL TOAST STYLES (Add to globals.css if needed)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Add these styles to your globals.css for enhanced animations:
 *
 * [data-sonner-toaster] [data-sonner-toast] {
 *   animation: toast-slide-in 0.3s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
 * }
 *
 * [data-sonner-toaster] [data-sonner-toast][data-removed="true"] {
 *   animation: toast-slide-out 0.2s ease-out forwards;
 * }
 *
 * @keyframes toast-slide-in {
 *   from {
 *     opacity: 0;
 *     transform: translateX(100%);
 *   }
 *   to {
 *     opacity: 1;
 *     transform: translateX(0);
 *   }
 * }
 *
 * @keyframes toast-slide-out {
 *   from {
 *     opacity: 1;
 *     transform: translateX(0);
 *   }
 *   to {
 *     opacity: 0;
 *     transform: translateX(100%);
 *   }
 * }
 */

export default ToastProvider;
